import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  IconButton, 
  List, 
  ListItem, 
  Avatar, 
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  Description as DocumentIcon,
  EmojiEmotions as StickerIcon,
  Gif as AnimationIcon,
  Mic as VoiceIcon,
  Videocam as VideoNoteIcon,
  LocationOn as LocationIcon,
  ContactPhone as ContactIcon,
  Poll as PollIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tgAccountsAPI } from '../../Api';
import PlumSvg from '../component/PlumSvg';

const TGAccountChatView = () => {
  const { id, chatId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [account, setAccount] = useState(null);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (type, message) => {
    setAlert(<Alert severity={type}>{message}</Alert>);
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAccount = async () => {
    try {
      const res = await tgAccountsAPI.getAccountById(id);
      setAccount(res.data?.data);
    } catch (e) {
      showAlert('error', 'Ошибка загрузки аккаунта');
    }
  };

  const loadChatInfo = async () => {
    try {
      const res = await tgAccountsAPI.getChats(id, 100);
      if (res.data?.success) {
        const foundChat = res.data.data.chats.find(c => c.id == chatId);
        if (foundChat) {
          setChat(foundChat);
        }
      }
    } catch (e) {
      console.warn('Failed to load chat info:', e?.message);
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await tgAccountsAPI.getChatHistory(id, chatId, 50);
      if (res.data?.success) {
        setMessages(res.data.data.messages || []);
      } else {
        showAlert('error', 'Ошибка загрузки сообщений');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка загрузки сообщений');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const res = await tgAccountsAPI.sendMessage(id, chatId, newMessage.trim());
      if (res.data?.success) {
        setNewMessage('');
        // Reload messages to show the new one
        setTimeout(() => loadMessages(), 500);
      } else {
        showAlert('error', 'Ошибка отправки сообщения');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    loadAccount();
    loadChatInfo();
    loadMessages();
  }, [id, chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (messageDate.getTime() === today.getTime() - 86400000) {
      return `Вчера ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString('ru-RU', { 
        day: '2-digit', 
        month: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getMessageIcon = (contentType) => {
    switch (contentType) {
      case 'photo': return <ImageIcon sx={{ fontSize: 16 }} />;
      case 'video': return <VideoIcon sx={{ fontSize: 16 }} />;
      case 'audio': return <AudioIcon sx={{ fontSize: 16 }} />;
      case 'document': return <DocumentIcon sx={{ fontSize: 16 }} />;
      case 'sticker': return <StickerIcon sx={{ fontSize: 16 }} />;
      case 'animation': return <AnimationIcon sx={{ fontSize: 16 }} />;
      case 'voice': return <VoiceIcon sx={{ fontSize: 16 }} />;
      case 'video_note': return <VideoNoteIcon sx={{ fontSize: 16 }} />;
      case 'location': return <LocationIcon sx={{ fontSize: 16 }} />;
      case 'contact': return <ContactIcon sx={{ fontSize: 16 }} />;
      case 'poll': return <PollIcon sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  const getChatIcon = (chatType) => {
    switch (chatType) {
      case 'chatTypePrivate':
        return <PersonIcon />;
      case 'chatTypeBasicGroup':
      case 'chatTypeSupergroup':
        return <GroupIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const renderMessageContent = (content) => {
    if (content.type === 'text') {
      return (
        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
          {content.text}
        </Typography>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getMessageIcon(content.type)}
          <Typography variant="body2" color="text.secondary">
            {content.description}
          </Typography>
          {content.caption && (
            <Typography variant="body2" sx={{ ml: 1 }}>
              {content.caption}
            </Typography>
          )}
        </Box>
      );
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 2, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper elevation={2} sx={{ p: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/tg-accounts/${id}/chats`)}>
              <ArrowBackIcon />
            </IconButton>
            
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {chat ? getChatIcon(chat.type) : <PersonIcon />}
            </Avatar>
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                {chat?.title || `Chat ${chatId}`}
              </Typography>
              {account && (
                <Typography variant="caption" color="text.secondary">
                  От: {account.phone} • {account.type}
                </Typography>
              )}
            </Box>

            <Tooltip title="Обновить сообщения">
              <IconButton onClick={loadMessages} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {alert}

        {/* Messages Area */}
        <Paper 
          elevation={1} 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              p: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Нет сообщений
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Начните общение, отправив первое сообщение
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {messages.map((message, index) => (
                  <React.Fragment key={message.id}>
                    <ListItem 
                      sx={{ 
                        display: 'flex',
                        flexDirection: message.is_outgoing ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        py: 1
                      }}
                    >
                      <Box 
                        sx={{ 
                          maxWidth: '70%',
                          bgcolor: message.is_outgoing ? 'primary.light' : 'grey.100',
                          borderRadius: 2,
                          p: 1.5,
                          ml: message.is_outgoing ? 0 : 1,
                          mr: message.is_outgoing ? 1 : 0
                        }}
                      >
                        {/* Sender info for incoming messages */}
                        {!message.is_outgoing && message.sender && (
                          <Typography 
                            variant="caption" 
                            color="primary.main" 
                            sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                          >
                            {message.sender.full_name || message.sender.username || `User ${message.sender.id}`}
                          </Typography>
                        )}

                        {/* Message content */}
                        {renderMessageContent(message.content)}

                        {/* Message time and status */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(message.date)}
                          </Typography>
                          {message.edit_date > 0 && (
                            <Chip 
                              label="изм." 
                              size="small" 
                              sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                    {index < messages.length - 1 && (
                      <Divider sx={{ my: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
                <div ref={messagesEndRef} />
              </List>
            )}
          </Box>

          {/* Message Input */}
          <Divider />
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Введите сообщение..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      color="primary"
                    >
                      {sending ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TGAccountChatView;
